import { Injectable } from '@angular/core';
import { FavoritesService } from '@products/services/favorites.service';
import { SiteConfigService } from '@services/site-config.service';
import { FilterStateManagerService } from '@shared/services/filter-state-manager.service';
import { Logger } from '@utils/logger';
import { FundListingComponentConfig } from '../fund-listing-component.config';
import {
  BaseFilter,
  FundFilter,
  FundFilterService,
} from './fund-filter.service';

const logger = Logger.getLogger('ProductFilterStateManagerService');

@Injectable({
  providedIn: 'root',
})
export class ProductFilterStateManagerService extends FilterStateManagerService {
  private componentConfiguration: FundListingComponentConfig;
  private filtersCreated = false;
  private filtersPreCalculated: boolean;
  constructor(
    fundFilterService: FundFilterService,
    private siteConfigService: SiteConfigService,
    private favoriteService: FavoritesService
  ) {
    super(fundFilterService);
    this.filtersPreCalculated =
      this.siteConfigService.product.ppss?.filtersOptions !== undefined;
  }

  mapFilterState(fundListingComponentConfig: FundListingComponentConfig) {
    this.componentConfiguration = fundListingComponentConfig;
    super.mapState();
    if (this.filtersPreCalculated) {
      this.createFilters();
    }
  }

  protected createFilters(): void {
    if (this.filtersCreated) {
      return;
    }

    this.favoriteService?.hasFavorites$().subscribe((hasFavorites) => {
      if (!hasFavorites && !this.siteConfigService.isNjBest()) {
        // NGC-14509: Remove Primary Share Class Default Filter for NjBest
        // to display all funds by default
        this.defaultSelectedFilters.set(FundFilter.PRIMARY_SHARE_CLASS, [
          'true',
        ]);
      }
      // WDE-2068: When we have favorite funds enabled,
      // we will display all favorite fund irrespective of primary or non-primary shareclass
      // followed by primary share class funds
      else if (hasFavorites && !this.siteConfigService.isNjBest()) {
        this.defaultSelectedFilters.set(
          FundFilter.FAV_FUND_WITH_PRIMARY_SHARECLASS,
          ['true']
        );
      }
    });

    const primaryFilters: FundFilter[] = this.siteConfigService.product?.ppss.primaryFilters[
      this.componentConfiguration.configurationName
    ]
      .map((key) => FundFilter[key])
      .filter((fundFilter) => fundFilter);

    const additionalFilters: FundFilter[] = this.siteConfigService.product?.ppss.additionalFilters[
      this.componentConfiguration.configurationName
    ]
      .map((key) => FundFilter[key])
      .filter((fundFilter) => fundFilter);

    if (this.filtersPreCalculated) {
      try {
        // set this.filtersWithValues from siteConfig
        [...primaryFilters, ...additionalFilters].forEach((filter) => {
          this.filtersWithValues.set(
            filter,
            new Map(
              Object.entries(
                this.siteConfigService.product.ppss.filtersOptions[
                  this.componentConfiguration.configurationName
                ][filter.key]
              )
            )
          );
        });
      } catch (e) {
        // pre calced filters not up-to-date
        logger.error('need to regenerate pre calced filters in site-config', e);
        this.filtersPreCalculated = false;
        return;
      }
    } else {
      // set this.filtersWithValues from data
      this.formatProductDataByFilters([
        ...primaryFilters,
        ...additionalFilters,
      ]);
    }
    primaryFilters.forEach((filterListItem: BaseFilter) => {
      this.state.primaryFilters.push(this.createFilterType(filterListItem)); // creates filter from filter config and this.filtersWithValues
    });

    additionalFilters.forEach((filterListItem: BaseFilter) => {
      this.state.additionalFilters.push(this.createFilterType(filterListItem));
    });

    // TODO: pick config from right path i.e., decorated config.
    this.state.morningStarFilter = {
      label: FundFilter.MORNINGSTAR_RATING.key,
      type: FundFilter.MORNINGSTAR_RATING,
      selected: [],
    };
    this.state.searchKeyword = {
      label: FundFilter.SEARCH_KEYWORD.key,
      type: FundFilter.SEARCH_KEYWORD,
    };
    this.filtersCreated = true;
    if (this.filtersPreCalculated) {
      this.sendNext('preCalcFilters');
    }
  }

  public mapPreFilterState(
    fundListingComponentConfig: FundListingComponentConfig,
    selectedPreFilter: string
  ) {
    this.componentConfiguration = fundListingComponentConfig;
    this.selectedPreFilter = selectedPreFilter;
    this.mapState();
    if (this.filtersPreCalculated) {
      this.createFilters();
    }
  }
}
